import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Users, Plus, Minus, Check, ArrowRight, ArrowLeft, Shield } from 'lucide-react';
import sixthLogo from '../assets/icon.svg';
import { local } from '../utils/constants';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Switch } from '../components/ui/Switch';
import { cn } from '../lib/utils';

const serverBaseUrl = local ? "http://127.0.0.1:8000" : "https://backend.withsix.co";

const NewTeams = () => {
  const navigate = useNavigate();
  const [teamName, setTeamName] = useState('');
  const [members, setMembers] = useState(2);
  const [isYearly, setIsYearly] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Pricing per seat
  const monthlyPricePerSeat = 40;
  const yearlyPricePerSeat = 32; // 20% off yearly

  const pricePerSeat = isYearly ? yearlyPricePerSeat : monthlyPricePerSeat;
  const totalMonthly = members * pricePerSeat;
  const totalAmount = isYearly ? totalMonthly * 12 : totalMonthly;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!teamName.trim()) {
      setError('Please enter a team name');
      return;
    }

    if (members < 2) {
      setError('Minimum 2 team members required');
      return;
    }

    const token = localStorage.getItem('jwt_token') || localStorage.getItem('web_access_token') || localStorage.getItem('vscode_jwt_token') || sessionStorage.getItem('jwt_token');

    if (!token) {
      navigate('/vscode/signin?redirect=' + encodeURIComponent('/new_teams'));
      return;
    }

    setLoading(true);

    // Open window immediately on user click to avoid popup blockers
    const checkoutWindow = window.open('about:blank', '_blank');

    try {
      const response = await fetch(`${serverBaseUrl}/teams/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          team_name: teamName.trim(),
          members: members,
          billing_preference: isYearly ? 'yearly' : 'monthly'
        })
      });

      if (response.ok) {
        const data = await response.json();

        // Store team info for the invite page
        localStorage.setItem('pending_team_id', data.team_id);
        localStorage.setItem('pending_team_name', teamName.trim());
        localStorage.setItem('pending_team_seats', members.toString());
        localStorage.setItem('pending_team_billing', isYearly ? 'yearly' : 'monthly');
        if (data.invite_code) {
          localStorage.setItem('pending_team_invite_code', data.invite_code);
        }

        // Redirect to Stripe checkout if URL provided, otherwise go to invite page
        if (data.checkout_url) {
          if (checkoutWindow) {
            checkoutWindow.location.href = data.checkout_url;
          } else {
            // Fallback if popup was blocked
            window.location.href = data.checkout_url;
          }
        } else {
          checkoutWindow?.close();
          navigate('/invite_members');
        }
      } else {
        checkoutWindow?.close();
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.message || errorData.detail || 'Failed to create team. Please try again.');
      }
    } catch (err) {
      checkoutWindow?.close();
      console.error('Team creation error:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-foreground font-sans overflow-x-hidden selection:bg-primary selection:text-white flex flex-col relative">
       {/* Background Orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <motion.div 
          animate={{ x: [0, 30, -20, 0], y: [0, -40, -60, 0], scale: [1, 1.05, 1.1, 1] }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -top-20 -left-20 w-[500px] h-[500px] rounded-full bg-primary/20 blur-[120px]" 
        />
        <motion.div 
          animate={{ x: [0, -30, 20, 0], y: [0, 40, 60, 0], scale: [1, 1.1, 0.95, 1] }}
          transition={{ duration: 25, repeat: Infinity, ease: "easeInOut", delay: 2 }}
          className="absolute top-1/2 -right-20 w-[400px] h-[400px] rounded-full bg-purple-600/20 blur-[100px]" 
        />
        <motion.div 
          animate={{ x: [0, 40, -30, 0], y: [0, -30, 20, 0] }}
          transition={{ duration: 18, repeat: Infinity, ease: "easeInOut", delay: 1 }}
          className="absolute bottom-0 left-1/4 w-[350px] h-[350px] rounded-full bg-indigo-500/20 blur-[100px]" 
        />
      </div>

      <header className="fixed top-0 left-0 right-0 z-50 border-b border-white/5 bg-[#0a0a0f]/80 backdrop-blur-md">
        <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6 lg:px-8">
          <Link to="/" className="flex items-center gap-2 text-xl font-bold bg-gradient-to-br from-[#E005FF] to-[#A73CFF] bg-clip-text text-transparent">
            <img src={sixthLogo} alt="Sixth AI" className="h-8 w-8" />
            Sixth AI
          </Link>
          <Link to="/pricing">
            <Button variant="ghost" className="text-muted-foreground hover:text-white hover:bg-white/5 gap-2">
              <ArrowLeft className="h-4 w-4" /> Back to Pricing
            </Button>
          </Link>
        </nav>
      </header>

      <main className="flex-1 flex items-center justify-center p-6 pt-24 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-2xl"
        >
          <Card className="bg-[#121219]/90 border-white/10 backdrop-blur-xl shadow-2xl">
            <CardHeader className="text-center pb-8">
              <div className="mx-auto w-20 h-20 rounded-2xl bg-gradient-to-br from-primary/20 to-purple-600/10 border border-primary/30 flex items-center justify-center mb-6 text-primary">
                <Users className="h-10 w-10" />
              </div>
              <CardTitle className="text-3xl font-bold text-white mb-2">Create Your Team</CardTitle>
              <CardDescription className="text-base text-muted-foreground">
                Set up your team workspace and invite members to collaborate with powerful AI tools.
              </CardDescription>
            </CardHeader>

            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-8">
                <AnimatePresence>
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="rounded-lg bg-red-500/10 border border-red-500/20 p-4 text-red-200 flex items-center gap-3 text-sm"
                    >
                      <Shield className="h-4 w-4 shrink-0" />
                      {error}
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-white">Team Name</label>
                  <Input
                    type="text"
                    placeholder="Enter your team or company name"
                    value={teamName}
                    onChange={(e) => setTeamName(e.target.value)}
                    className="bg-black/20 border-white/10 text-white placeholder:text-white/30 h-12"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-white">Number of Seats (Minimum 2)</label>
                  <div className="flex items-center gap-4">
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => setMembers(Math.max(2, members - 1))}
                      disabled={members <= 2}
                      className="h-12 w-12 border-white/10 hover:bg-white/5 hover:text-white"
                    >
                      <Minus className="h-5 w-5" />
                    </Button>
                    <div className="flex-1 text-center bg-black/20 rounded-lg h-12 flex items-center justify-center border border-white/5">
                      <span className="text-xl font-bold text-white mr-2">{members}</span>
                      <span className="text-sm text-muted-foreground">team members</span>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => setMembers(members + 1)}
                      className="h-12 w-12 border-white/10 hover:bg-white/5 hover:text-white"
                    >
                      <Plus className="h-5 w-5" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-white">Billing Preference</label>
                  <div className="flex items-center justify-center gap-4 p-4 bg-black/20 rounded-xl border border-white/5">
                    <span className={cn("text-sm font-medium transition-colors", !isYearly ? "text-white" : "text-muted-foreground")}>Monthly</span>
                    <Switch 
                      checked={isYearly} 
                      onCheckedChange={setIsYearly}
                      className="data-[state=checked]:bg-primary"
                    />
                    <span className={cn("text-sm font-medium transition-colors", isYearly ? "text-white" : "text-muted-foreground")}>Yearly</span>
                    {isYearly && (
                      <Badge className="bg-green-500/10 text-green-500 border-green-500/20 ml-2">
                        Save 20%
                      </Badge>
                    )}
                  </div>
                </div>

                <div className="bg-white/5 rounded-xl p-6 border border-white/5 space-y-4">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground">Price per seat</span>
                    <span className="text-white font-medium">${pricePerSeat}/mo</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground">Number of seats</span>
                    <span className="text-white font-medium">{members}</span>
                  </div>
                  <div className="pt-4 border-t border-white/10 flex justify-between items-center">
                    <span className="font-bold text-white">Total {isYearly ? '(billed annually)' : '(billed monthly)'}</span>
                    <span className="text-2xl font-bold text-primary">
                      ${totalAmount}<span className="text-base font-normal text-muted-foreground">{isYearly ? '/year' : '/mo'}</span>
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    "Unlimited AI completions", "All premium models", "Team admin dashboard",
                    "Priority support", "Centralized billing", "Usage analytics"
                  ].map((feature, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Check className="h-4 w-4 text-green-500 shrink-0" /> {feature}
                    </div>
                  ))}
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-14 text-lg font-bold bg-gradient-to-r from-[#E005FF] to-[#A73CFF] hover:opacity-90 border-0 shadow-lg shadow-primary/25"
                  disabled={loading}
                >
                  {loading ? 'Creating Team...' : 'Continue to Payment'}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      </main>
    </div>
  );
};

export default NewTeams;
